import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { forwardRef, MutableRefObject, useCallback, useRef, useState } from 'react';
import { IMarker } from 'src/interfaces/Marker';
import NotesListItem from '../../components/NotesListItem';
import { useListNavigationByKeyboard } from '../NotesMenu/hooks/useListNavigationByKeyboard';
import styles from './styles.module.scss';

export interface INotesListProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  locale?: string;
  notes: IMarker[];
  onTimeSelect: (time: number) => void;
  onDelete: (note: IMarker) => void;
  onEdit: (note: IMarker) => void;
  onRetry: (note: IMarker) => void;
}

const NotesList = forwardRef<HTMLDivElement, INotesListProps>(
  ({ className, notes, locale, onDelete, onEdit, onTimeSelect, onRetry, ...rest }, ref) => {
    const { t } = useTranslation('common');
    const { registerHTMLElement } = useListNavigationByKeyboard();
    const descriptionComponentRef = useRef<HTMLDivElement | null>(null);
    const [isDescriptionElementInViewPort, setIsDescriptionElementInViewPort] = useState(true);

    const isDescriptionElementVisibleInListViewPort = useCallback(() => {
      const listRef = ref as MutableRefObject<HTMLDivElement | null>;
      if (!listRef.current || !descriptionComponentRef.current) {
        return false;
      }

      return (
        listRef.current.getBoundingClientRect().top - descriptionComponentRef.current.getBoundingClientRect().bottom <=
        0
      );
    }, [ref]);

    const handleNotesListOnScroll = useCallback(() => {
      if (isDescriptionElementVisibleInListViewPort()) {
        setIsDescriptionElementInViewPort(true);
        return;
      }

      setIsDescriptionElementInViewPort(false);
    }, [isDescriptionElementVisibleInListViewPort]);

    return (
      <div
        role='list'
        ref={ref}
        className={classNames({ [styles.borderTop]: !isDescriptionElementInViewPort }, styles.container, className)}
        onScroll={handleNotesListOnScroll}
        {...rest}
      >
        <div ref={descriptionComponentRef} className={styles.description}>
          <p>{t('notes.description')}</p>
        </div>
        {notes.map((item, index) => (
          <NotesListItem
            {...registerHTMLElement({ startWithFocus: index === 0 })}
            key={item.id}
            note={item}
            locale={locale}
            onEdit={() => onEdit(item)}
            onDelete={() => {
              onDelete(item);
            }}
            onTimeSelect={() => {
              onTimeSelect(item.time);
            }}
            onRetry={() => {
              onRetry(item);
            }}
          />
        ))}
      </div>
    );
  },
);

NotesList.displayName = 'NotesList';
export default NotesList;
